import React, { useCallback, useMemo } from 'react';

import { Box, Tooltip } from '@chakra-ui/react';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { capitalize, startCase } from 'lodash';
import { useSelector } from 'react-redux';
import { Collaborators } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CommonTreeCell/Collabs';
import { ObjectiveKRCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CommonTreeCell/ObjectiveKRCell';
import { Owners } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CommonTreeCell/Owners';
import { OwnersAndCollaborators } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CommonTreeCell/OwnersAndCollaborators';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import { postProcessPopup } from 'screens/Team/Rabbit/Goals/AgGridTreeView/utils';
import { kRDrawerSecondaryPanelSelector } from 'selectors/krSecondaryPanel';

import { MODULE_KEYS_USER_PREFERENCE_PERSISTENCE, OBJECTIVE_LEVEL_TAGS } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { getPersistenceKey } from 'constants/helper';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';

import { RealignRadioButton } from './RealignRadioButton';

export const RealignGrid = ({
  gridData,
  gridRef,
  selectedParentUuid,
  setSelectedParentUuid,
  SelectionComponent = RealignRadioButton,
}) => {
  const {
    details: { parentUuid, okrUuid },
  } = useSelector(kRDrawerSecondaryPanelSelector);

  const { moduleName } = useOrganisationSettings();

  const columnDefs = useMemo(
    () => [
      // we're using the auto group column by default!
      {
        field: 'toIds',
        filter: 'agMultiColumnFilter',
        sortable: true,
        headerName: 'Team',
        minWidth: 150,
        // maxWidth: 200,
        filterParams: {
          filters: [
            {
              filter: 'agSetColumnFilter',
              display: 'accordion',
              title: 'Filter by Function',
              filterParams: {
                valueFormatter: ({ value }) => {
                  if (value) return value;
                  return '';
                },
                getValue: (data) => data.data?.toIds?.map(({ employeeFunction }) => employeeFunction)?.filter((v) => v),
                // eslint-disable-next-line react/no-unstable-nested-components
                cellRenderer: (params) => (
                  <Tooltip label={params?.value ?? ''}>
                    <p>{params?.value ?? '(Blanks)'}</p>
                  </Tooltip>
                ),
              },
            },
            {
              filter: 'agSetColumnFilter',
              display: 'accordion',
              title: 'Filter by Name',
              filterParams: {
                applyMiniFilterWhileTyping: true,
                valueFormatter: ({ value }) => {
                  if (value) return value;
                  return '(Blanks)';
                },
              },
            },
          ],
        },
        getValue: (data) => data.data?.toIds?.map(({ displayName }) => displayName),
        getQuickFilterText: (params) => {
          // This function provides text for quick filtering
          if (params.data?.toIds && Array.isArray(params.data.toIds)) {
            return params.data.toIds
              .map(({ displayName }) => displayName)
              .filter(Boolean)
              .join(' ');
          }
          return '';
        },
        cellRenderer: OwnersAndCollaborators,
      },
      {
        field: 'toIds',
        filter: 'agMultiColumnFilter',
        hide: true,
        sortable: true,
        minWidth: 140,
        maxWidth: 280,
        headerName: 'Owner',
        filterParams: {
          filters: [
            {
              filter: 'agSetColumnFilter',
              display: 'accordion',
              title: 'Filter by Function',
              filterParams: {
                // values: (params) => {
                //   console.log({ params });
                //   // return data.data?.toIds?.map(({ employeeFunction }) => `${employeeFunction}`);
                // },

                valueFormatter: ({ value }) => {
                  if (value) return value;
                  return '';
                },
                getValue: (data) =>
                  data.data?.toIds
                    ?.map(({ employeeFunction, ownerType }) => ownerType === 'OWNER' && employeeFunction)
                    ?.filter((v) => v),
                // eslint-disable-next-line react/no-unstable-nested-components
                cellRenderer: (params) => (
                  <Tooltip label={params?.value ?? ''}>
                    <p>{params?.value ?? '(Blanks)'}</p>
                  </Tooltip>
                ),
              },
            },
            {
              filter: 'agSetColumnFilter',
            },
          ],
        },
        valueGetter: (data) => data.data?.toIds?.map(({ displayName }) => displayName),
        cellRenderer: Owners,
      },
      {
        field: 'toIds',
        filter: 'agMultiColumnFilter',
        hide: true,
        sortable: true,
        headerName: 'Collaborators',
        minWidth: 160,
        maxWidth: 280,
        filterParams: {
          filters: [
            {
              filter: 'agSetColumnFilter',
              display: 'accordion',
              title: 'Filter by Function',
              filterParams: {
                // values: (params) => {
                //   console.log({ params });
                //   // return data.data?.toIds?.map(({ employeeFunction }) => `${employeeFunction}`);
                // },

                valueFormatter: ({ value }) => {
                  if (value) return value;
                  return '';
                },
                getValue: (data) =>
                  data.data?.toIds
                    ?.map(({ employeeFunction, ownerType }) => ownerType === 'COLLABORATOR' && employeeFunction)
                    ?.filter((v) => v),
                // eslint-disable-next-line react/no-unstable-nested-components
                cellRenderer: (params) => (
                  <Tooltip label={params?.value ?? ''}>
                    <p>{params?.value ?? '(Blanks)'}</p>
                  </Tooltip>
                ),
              },
            },
            {
              filter: 'agSetColumnFilter',
              filterParams: {
                valueFormatter: ({ value }) => {
                  if (value) return value;
                  return '(Blanks)';
                },
              },
            },
          ],
        },
        valueGetter: (data) =>
          data.data?.toIds?.map(({ displayName, ownerType }) => ownerType === 'COLLABORATOR' && displayName),
        cellRenderer: Collaborators,
      },

      {
        headerName: '',
        minWidth: 36,
        maxWidth: 36,
        // eslint-disable-next-line react/no-unstable-nested-components
        cellRenderer: (props) => (
          <SelectionComponent
            rowData={props.data}
            parentUuid={parentUuid}
            okrUuid={okrUuid}
            selectedParentUuid={selectedParentUuid}
            setSelectedParentUuid={setSelectedParentUuid}
          />
        ),
        lockVisible: true,
        lockPosition: 'left',
        suppressMovable: true,
        suppressFiltersToolPanel: true,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
      },
    ],
    [parentUuid, selectedParentUuid, setSelectedParentUuid]
  );

  const autoGroupColumnDef = useMemo(
    () => ({
      headerName: modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE),
      minWidth: 350,
      maxWidth: 350,
      flex: 1,
      filter: 'agMultiColumnFilter',
      filterParams: {
        filters: [
          {
            filter: 'agSetColumnFilter',
            display: 'accordion',
            title: 'Filter by Level',
            filterParams: {
              values: OBJECTIVE_LEVEL_TAGS.map(({ value }) => value),
              // this is used to display the value in the filter menu
              valueFormatter: ({ value }) => capitalize(startCase(value)),
              // this is used to get the key which is used to filter the data
              getValue: (data) => data.data.objectiveType,
            },
          },
          {
            getValue: (data) => data.data.title,
            filterParams: {
              // copy pasta from ag grid.
              // not touching it for now
              textFormatter: (r) => {
                if (r === null) return null;
                return r
                  .toLowerCase()
                  .replace(/[àáâãäå]/g, 'a')
                  .replace(/æ/g, 'ae')
                  .replace(/ç/g, 'c')
                  .replace(/[èéêë]/g, 'e')
                  .replace(/[ìíîï]/g, 'i')
                  .replace(/ñ/g, 'n')
                  .replace(/[òóôõö]/g, 'o')
                  .replace(/œ/g, 'oe')
                  .replace(/[ùúûü]/g, 'u')
                  .replace(/[ýÿ]/g, 'y');
              },
              filterOptions: ['contains', 'notContains'],
              debounceMs: 200,
              suppressAndOrCondition: true,
            },
          },
          // {
          //   filter: 'agSetColumnFilter'
          // }
        ],
      },
      valueGetter: (data) => data.data.title,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: ObjectiveKRCell,
      },
      cellClass: 'objective-kr-cell-ag-grid-wrap',
      autoHeight: true,
      wrapText: true,
    }),
    [moduleName]
  );

  const defaultColDef = useMemo(
    () => ({
      flex: 0,
      menuTabs: ['filterMenuTab'],
      headerComponent: CustomHeaderCell,
    }),
    []
  );

  const getDataPath = useMemo(() => (data) => data.hierarchy, []);

  const onGridReady = useCallback(
    (params) => {
      params.api.setFilterModel({
        objectiveTimeBoundId: {
          filter: parentUuid,
          type: 'equals',
        },
      });

      const filteredNodes = [];
      params.api.forEachNodeAfterFilter((node) => {
        filteredNodes.push(node);
      });

      if (filteredNodes.length > 0) {
        params.api.ensureIndexVisible(filteredNodes[0].rowIndex, 'middle');
      }

      // Don't forget to clear the filter afterwards if needed
      params.api.setFilterModel(null);

      gridRef?.current?.api?.collapseAll();
    },
    [gridRef, parentUuid]
  );

  const getRowId = useCallback(({ data }) => data?.uuid, []);

  /* Grid State persistence Key */
  const gridStatePersistenceKey = useMemo(() => {
    // eslint-disable-next-line no-shadow
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.GOAL;
    const viewId = null;
    const tabId = null;
    const uniqueId = 'RealignGrid';

    return getPersistenceKey({
      moduleName,
      viewId,
      tabId,
      uniqueId,
    });
  }, []);

  return (
    <Box
      width="100%"
      flex={1}
      px={2}
      sx={{
        '.goals-ag-grid': {
          height: '100% !important',
        },
      }}
    >
      <AgGridTable
        useFilterModelPersistence={false}
        useColumnStatePersistence={false}
        gridStatePersistenceKey={gridStatePersistenceKey}
        ref={gridRef}
        rowData={gridData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        autoGroupColumnDef={autoGroupColumnDef}
        treeData
        animateRows
        groupDefaultExpanded={-1}
        getDataPath={getDataPath}
        domLayout="normal"
        rowHeight={74}
        rowStyle={{ cursor: 'pointer' }}
        getRowId={getRowId}
        onRowDataUpdated={onGridReady}
        suppressMenuHide
        postProcessPopup={postProcessPopup}
        showSidebarToggle={false}
        additionalMobileProps={{ pinned: undefined, lockPosition: undefined }}
      />
    </Box>
  );
};
